import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CSSProperties, useEffect, useMemo, useRef } from 'react';

export interface MapStop {
  lat: number;
  lon: number;
  label: string;
  kind: 'pickup' | 'pooled-pickup' | 'dropoff' | 'route-start';
}

export interface MapLine {
  points: [number, number][];
  kind: 'driver-path' | 'stop-order' | 'ride';
}

// Shape carries the meaning, not colour — but the basemap keeps its own colours, so a marker is
// drawn in the theme's ink with a contrasting ring to stay legible over roads and parkland.
const markerStyles = (
  ink: string,
  ring: string
): Record<MapStop['kind'], CSSProperties> => ({
  pickup: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: ink,
    border: `2px solid ${ring}`
  },
  'pooled-pickup': {
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: ink,
    border: `2px dashed ${ring}`,
    outline: `2px solid ${ink}`
  },
  dropoff: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: ring,
    border: `2.5px solid ${ink}`
  },
  'route-start': {
    width: 12,
    height: 12,
    background: ink,
    border: `2px solid ${ring}`,
    transform: 'rotate(45deg)'
  }
});

const cssText = (style: CSSProperties): string =>
  Object.entries(style)
    .map(([property, value]) => {
      const name = property.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
      return `${name}:${typeof value === 'number' ? `${value}px` : value}`;
    })
    .join(';');

// 'driver-path' is real road geometry and is the only line drawn solid — a solid heavy line
// reads as "this is the road driven", so a schematic must never borrow that weight.
// 'stop-order' connects stops in sequence and stays thin and dashed to look like the diagram
// it is.
const lineStyles = (ink: string, dim: string): Record<MapLine['kind'], L.PolylineOptions> => ({
  'driver-path': { color: ink, weight: 3, opacity: 0.95 },
  'stop-order': { color: dim, weight: 1.5, opacity: 0.6, dashArray: '2 6' },
  ride: { color: dim, weight: 1.75, opacity: 0.8, dashArray: '4 4' }
});

const LEGEND: { kind: MapStop['kind']; text: string }[] = [
  { kind: 'pickup', text: 'pickup' },
  { kind: 'pooled-pickup', text: 'pooled pickup' },
  { kind: 'dropoff', text: 'dropoff' },
  { kind: 'route-start', text: 'route start' }
];

const markerIcon = (style: CSSProperties) =>
  L.divIcon({
    className: '',
    html: `<div style="${cssText(style)}"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  });

export const RoutingMap = ({ stops, lines = [] }: { stops: MapStop[]; lines?: MapLine[] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const theme = useTheme();
  const ink = theme.palette.text.primary;
  const ring = theme.palette.background.paper;
  const markerStyle = useMemo(() => markerStyles(ink, ring), [ink, ring]);
  const lineStyle = useMemo(() => lineStyles(ink, theme.palette.text.secondary), [
    ink,
    theme.palette.text.secondary
  ]);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, { attributionControl: true });
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = L.layerGroup().addTo(map);
    lines.forEach(line => L.polyline(line.points, lineStyle[line.kind]).addTo(layer));
    stops.forEach(stop => {
      L.marker([stop.lat, stop.lon], { icon: markerIcon(markerStyle[stop.kind]) })
        .bindTooltip(stop.label)
        .addTo(layer);
    });
    const bounds = stops.map(stop => [stop.lat, stop.lon] as [number, number]);
    // Fit on the next frame — fitting before the container has been laid out max-zooms the map.
    const frame = requestAnimationFrame(() => {
      map.invalidateSize();
      if (bounds.length) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
      else map.setView([40.73, -73.99], 11);
    });
    return () => {
      cancelAnimationFrame(frame);
      layer.remove();
    };
  }, [stops, lines, markerStyle, lineStyle]);

  return (
    <Box>
      <Stack direction="row" spacing={2.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {LEGEND.map(entry => (
          <Stack key={entry.kind} direction="row" spacing={0.75} alignItems="center">
            <div style={markerStyle[entry.kind]} />
            <Typography variant="caption" color="text.secondary">
              {entry.text}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Box
        ref={containerRef}
        data-testid="routing-map"
        sx={{
          // Shorter on a phone, where 460px would be most of the screen and the page under it
          // has to stay reachable past the map's own touch handling.
          height: { xs: 320, sm: 400, md: 460 },
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          // The basemap keeps its own colours in both themes — roads, water and parks are the
          // point of having a map.
          '& .leaflet-container': { background: theme.palette.background.paper }
        }}
      />
    </Box>
  );
};

export default RoutingMap;
